export interface HilToastDedupeInput {
  /** Explicit override — merged with derived keys. */
  dedupeKey?: string;
  /** AgOps hil_request UUID when known. */
  hilId?: string;
  requestId?: string;
  agentId?: string;
  conversationId?: string;
  /** Run/task scope — relay SSE uses task_id when hil id is absent. */
  taskId?: string;
}

/**
 * Builds all keys that should suppress duplicate HIL toasts within the dedupe window.
 * AgOps hil_request ids dedupe per request; relay-only events dedupe per run/task.
 */
export function resolveHilToastDedupeKeys(input: HilToastDedupeInput): string[] {
  const keys = new Set<string>();
  const add = (value: string | undefined): void => {
    const trimmed = (value ?? '').trim();
    if (trimmed) {
      keys.add(trimmed);
    }
  };

  const hilId = (input.hilId ?? '').trim();
  const conversationId = (input.conversationId ?? '').trim();
  const taskId = (input.taskId ?? '').trim();

  add(input.dedupeKey);
  add(hilId || undefined);
  add(input.requestId);

  const agentId = (input.agentId ?? '').trim();
  const requestId = (input.requestId ?? '').trim();
  if (agentId && agentId !== requestId) {
    add(agentId);
  }

  if (conversationId && hilId) {
    keys.add(`hil-toast:conv:${conversationId}:hil:${hilId}`);
  }

  // Relay SSE may emit several hil_escalation lines for one run without hil ids.
  // Scope dedupe to the run only for those events so they do not block AgOps requests.
  if (!hilId && conversationId && taskId) {
    keys.add(`hil-toast:conv:${conversationId}:task:${taskId}`);
  }

  return [...keys];
}

export function resolveHilToastId(input: HilToastDedupeInput, dedupeKeys: string[]): string {
  const hilId = (input.hilId ?? '').trim();
  if (hilId) {
    return `hil-raised-${hilId}`;
  }

  const runScoped = dedupeKeys.find(key => key.startsWith('hil-toast:conv:'));
  if (runScoped) {
    return `hil-raised-${runScoped}`;
  }

  const primary = dedupeKeys[0]?.trim();
  return primary ? `hil-raised-${primary}` : `hil-raised-${Date.now()}`;
}

/** True when the event represents a persisted AgOps hil_request (not relay chat noise). */
export function isAgOpsHilRequestToast(input: HilToastDedupeInput): boolean {
  return Boolean((input.hilId ?? '').trim());
}
